import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Turnstile, { TURNSTILE_ENABLED } from "../components/Turnstile";
import { debugLog } from "../lib/logger";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState("");
  const [challenge, setChallenge] = useState(false);
  const tsRef = useRef(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [googleCfg, setGoogleCfg] = useState({ enabled: false, client_id: "" });

  useEffect(() => { api.get("/auth/google/config").then((r) => setGoogleCfg(r.data)).catch(() => {}); }, []);

  const checkChallenge = async (em) => {
    if (!em) return;
    try {
      const { data } = await api.get(`/auth/challenge-status?email=${encodeURIComponent(em)}`);
      setChallenge(Boolean(data.required));
    } catch (e) { debugLog("challenge status check failed", e); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password, turnstile_token: token });
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : data.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      setError(apiError(err));
      setToken("");
      tsRef.current?.reset();
      checkChallenge(email);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (googleCfg.enabled && googleCfg.client_id) {
      const redirectUri = window.location.origin + "/auth/google";
      const state = Math.random().toString(36).slice(2);
      sessionStorage.setItem("g_oauth_state", state);
      const p = new URLSearchParams({
        client_id: googleCfg.client_id, redirect_uri: redirectUri, response_type: "code",
        scope: "openid email profile", state, prompt: "select_account", access_type: "online",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${p.toString()}`;
      return;
    }
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="max-w-5xl w-full mx-auto px-6 pt-8">
        <Link to="/" className="flex items-center gap-3 w-fit" data-testid="login-home-link">
          <span className="solera-symbol" />
          <span className="wordmark text-bone text-sm">Solera</span>
          <span className="t-cap text-muted-s ml-2">solera.vc</span>
        </Link>
      </header>
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="gapped-enclosure w-full max-w-md p-10 rise" data-testid="login-panel">
          <span className="t-label">Access by referral</span>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input
              className="input-solera" type="email" placeholder="Email" value={email}
              onChange={(e) => setEmail(e.target.value)} onBlur={() => checkChallenge(email)} required data-testid="login-email-input"
            />
            <input
              className="input-solera" type="password" placeholder="Password" value={password}
              onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
            />
            {error && <p className="t-small status-blocked" data-testid="login-error">{error}</p>}
            {challenge && TURNSTILE_ENABLED && <Turnstile ref={tsRef} onToken={setToken} />}
            <button className="btn-primary w-full" disabled={busy || (challenge && TURNSTILE_ENABLED && !token)} data-testid="login-submit-button">
              {busy ? "Verifying" : "Continue"}
            </button>
          </form>
          <div className="hairline my-6" />
          <button onClick={google} className="btn-ghost w-full" data-testid="login-google-button">
            Continue with Google
          </button>
          <p className="t-cap text-muted-s mt-8">
            Referred by a member?{" "}
            <Link to="/register" className="link-plain" data-testid="login-register-link">Complete registration</Link>
          </p>
          <p className="t-cap text-muted-s mt-2">
            <Link to="/forgot-password" className="link-plain" data-testid="login-forgot-link">Forgotten password</Link>
          </p>
        </div>
      </div>
      <footer className="max-w-5xl w-full mx-auto px-6 pb-10">
        <p className="t-cap text-muted-s measure">
          Solera will never ask you to send funds, account details or identity documents by email.
          This is the only domain we use: solera.vc.
        </p>
      </footer>
    </div>
  );
}
